import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Form, Table, Button } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import { FiSearch, FiEye, FiAlertTriangle, FiEdit2, FiBellOff, FiRotateCcw } from "react-icons/fi";
import Swal from "sweetalert2";
import Select from "react-select";

export default function StockIndex() {
  const { t } = useContext(TranslatorContext);
  const navigate = useNavigate();

  // State Management
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Inline Editing State
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingSalePrice, setEditingSalePrice] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [lowStockThreshold, setLowStockThreshold] = useState(3);

  const role = localStorage.getItem("role");
  const isAdmin = role?.toLowerCase() === "admin";

  useEffect(() => {
    fetchStock();
  }, []);

  const fetchStock = () => {
    setLoading(true);
    const token = localStorage.getItem("authToken");

    axios
      .get(`${process.env.REACT_APP_API_URL}/stock`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.status) {
          setData(res.data.stock || []);
          setCategories(res.data.categories || []);
          setVendors(res.data.vendor || []);
          setLowStockThreshold(res.data.low_stock_threshold ?? 3);
        } else {
          setData([]);
          setCategories([]);
          setVendors([]);
        }
      })
      .catch((err) => console.error("Error fetching stock:", err))
      .finally(() => setLoading(false));
  };

  const notify = (icon, title, text) => {
    Swal.fire({
      toast: true,
      position: "top-end",
      icon,
      title,
      text,
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
  };

  const handleLowStockAlert = (product) => {
    Swal.fire({
      title: t("Low Stock Alert!"),
      html: `<strong>${product.name}</strong> ${t("has only")} <strong>${product.available_stock}</strong> ${t("items left in stock")}.`,
      icon: "warning",
      confirmButtonText: t("View Details"),
      confirmButtonColor: "#3085d6",
      showCancelButton: true,
      cancelButtonText: t("Close"),
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        handleViewSales(product.id);
      }
    });
  };

  const toggleLowStockDismissal = async (product, dismiss) => {
    if (dismiss) {
      const confirmed = await Swal.fire({
        title: t("Dismiss low stock alert?"),
        html: `<strong>${product.name}</strong> ${t("will be hidden from the low stock list until it is restocked.")}`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: t("Yes, dismiss"),
        cancelButtonText: t("Cancel"),
        confirmButtonColor: "#d33",
      });

      if (!confirmed.isConfirmed) return;
    }

    const token = localStorage.getItem("authToken");
    const endpoint = dismiss ? "dismiss-low-stock" : "restore-low-stock";

    setUpdatingId(product.id);

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/stock/${product.id}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status) {
        setData((prevData) =>
          prevData.map((item) =>
            item.id === product.id ? { ...item, low_stock_dismissed: dismiss } : item
          )
        );

        notify(
          "success",
          dismiss ? t("Dismissed") : t("Restored"),
          response.data.message
        );
      }
    } catch (error) {
      console.error("Error updating low stock alert:", error);
      notify(
        "error",
        t("Update Failed"),
        error.response?.data?.message || t("Failed to update the low stock alert")
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleViewSales = (productId) => {
    navigate(`/stock/show/${productId}`);
  };

  // Dropdown Options
  const vendorOptions = vendors.map((v) => ({
    value: v.id,
    label: v.company_name,
  }));

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const lowStockCount = data.filter(
    (item) => item.low_stock && !item.low_stock_dismissed
  ).length;

  // Filtering Logic
  const filteredData = data.filter((item) => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory ? item.category_id == selectedCategory : true;
    const matchesVendor = selectedVendor
      ? item.vendor === vendorOptions.find((opt) => opt.value === selectedVendor)?.label
      : true;

    return matchesSearch && matchesCategory && matchesVendor;
  });

  // Inline Price Editing Actions
  const startEditSalePrice = (product) => {
    setEditingProductId(product.id);
    setEditingSalePrice(product.sale_price || "");
  };

  const cancelEditSalePrice = () => {
    setEditingProductId(null);
    setEditingSalePrice("");
  };

  const updateSalePrice = async (productId) => {
    if (!editingSalePrice || parseFloat(editingSalePrice) < 0) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Invalid Price",
        text: "Please enter a valid sale price (minimum 0)",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    const token = localStorage.getItem("authToken");

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/stock/${productId}/change-sale-price`,
        { sale_price: editingSalePrice },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status) {
        setData((prevData) =>
          prevData.map((item) =>
            item.id === productId ? { ...item, sale_price: editingSalePrice } : item
          )
        );
        cancelEditSalePrice();

        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Updated!",
          text: response.data.message || "Sale price updated successfully",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } catch (error) {
      console.error("Error updating sale price:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.message || "Failed to update sale price",
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      });
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t("loading")}...</span>
          </div>
          <p className="mt-3">{t("loading")}...</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Row>
        {/* Breadcrumb Section */}
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb p-3">
              <h3 className="mc-breadcrumb-title mb-0">{t("stock management")}</h3>
              <ul className="mc-breadcrumb-list mb-0">
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">{t("home")}</Link>
                </li>
                <li className="mc-breadcrumb-item">
                  <Link to="#" className="mc-breadcrumb-link">{t("inventory")}</Link>
                </li>
                <li className="mc-breadcrumb-item">{t("stock")}</li>
              </ul>
            </div>
          </div>
        </Col>

        {/* Stock List Card */}
        <Col xl={12}>
          <div className="mc-card">
            {/* Header Filters & Search */}
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <h5 className="mb-0">{t("stock list")}</h5>
                <Link
                  to="/low-stock-products"
                  className="btn btn-sm btn-danger d-inline-flex align-items-center gap-2"
                >
                  <FiAlertTriangle size={14} />
                  {t("Low Stock Products")}
                  <span className="badge bg-danger">{lowStockCount}</span>
                </Link>
                <span className="text-muted small">
                  {t("Low stock at")} {lowStockThreshold} {t("or less")}
                </span>
              </div>
              <div className="d-flex gap-3 flex-wrap">
                {/* Category Select */}
                <Select
                  value={categoryOptions.find((opt) => opt.value === selectedCategory) || null}
                  onChange={(option) => setSelectedCategory(option ? option.value : "")}
                  options={categoryOptions}
                  isClearable
                  isSearchable
                  placeholder={t("All categories")}
                  styles={{ container: (base) => ({ ...base, width: 200 }) }}
                />

                {/* Vendor Select */}
                <Select
                  value={vendorOptions.find((opt) => opt.value === selectedVendor) || null}
                  onChange={(option) => setSelectedVendor(option ? option.value : "")}
                  options={vendorOptions}
                  isClearable
                  isSearchable
                  placeholder={t("All Vendor")}
                  styles={{ container: (base) => ({ ...base, width: 200 }) }}
                />

                {/* Search Box */}
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder={t("search products...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingRight: "35px", width: "250px" }}
                  />
                  <FiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" size={16} />
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="table-responsive-wrapper">
              <Table responsive hover className="table mb-0">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: "5%" }}>#</th>
                    <th style={{ width: "20%" }}>{t("product name")}</th>
                    {isAdmin && (
                      <>
                        <th style={{ width: "20%" }}>{t("Purchase Price")}</th>
                        <th style={{ width: "20%" }}>{t("Sale Price")}</th>
                      </>
                    )}
                    <th style={{ width: "15%" }}>{t("category")}</th>
                    <th className="text-center" style={{ width: "12%" }}>{t("available stock")}</th>
                    <th className="text-center" style={{ width: "13%" }}>{t("status")}</th>
                    <th className="text-center" style={{ width: "15%" }}>{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item) => (
                      <tr key={item.id}>
                        <td className="text-center">{item.id || "-"}</td>

                        <td>
                          <strong>{item.name}</strong> - {item.vendor || "-"}
                        </td>

                        {isAdmin && (
                          <>
                            {/* Purchase Prices List */}
                            <td>
                              {item.purchase_prices?.length ? (
                                item.available_stock < 3 ? (
                                  // Stock 3 se kam ho to sirf last purchase price
                                  <div>
                                    <span style={{ textDecoration: "underline" }}>
                                      {item.purchase_prices[item.purchase_prices.length - 1].purchase_price
                                        ? item.purchase_prices[item.purchase_prices.length - 1].purchase_price
                                        : "Free"}
                                    </span>
                                  </div>
                                ) : (
                                  // Normal stock par tamam purchase prices aur quantities
                                  item.purchase_prices.map((p, idx) => (
                                    <div key={idx}>
                                      <span style={{ textDecoration: "underline" }}>
                                        {p.purchase_price ? p.purchase_price : "Free"}
                                      </span>{" "}
                                      ({p.quantity})
                                    </div>
                                  ))
                                )
                              ) : (
                                "-"
                              )}
                            </td>

                            {/* Sale Price (Editable) */}
                            <td>
                              {editingProductId === item.id ? (
                                <div className="d-flex gap-1">
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    size="sm"
                                    value={editingSalePrice}
                                    onChange={(e) => setEditingSalePrice(e.target.value)}
                                    style={{ width: "100px" }}
                                    onKeyDown={(e) => e.key === "Enter" && updateSalePrice(item.id)}
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    variant="success"
                                    onClick={() => updateSalePrice(item.id)}
                                    style={{ padding: "0 8px" }}
                                  >
                                    ✓
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={cancelEditSalePrice}
                                    style={{ padding: "0 8px" }}
                                  >
                                    ✗
                                  </Button>
                                </div>
                              ) : (
                                <div className="d-flex align-items-center gap-2">
                                  <span style={{ textDecoration: "underline" }}>
                                    {item.sale_price || "-"}
                                  </span>
                                  <FiEdit2
                                    size={14}
                                    className="text-primary cursor-pointer"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => startEditSalePrice(item)}
                                    title="Click to edit sale price"
                                  />
                                </div>
                              )}
                            </td>
                          </>
                        )}

                        <td>
                          <span className="badge bg-light text-dark">
                            {item.category_name}
                          </span>
                        </td>

                        <td className="text-center">
                          <span className="badge bg-info text-white px-3 py-2">
                            {item.available_stock}
                          </span>
                        </td>

                        <td className="text-center">
                          {item.low_stock ? (
                            <span
                              className={`badge px-3 py-2 ${item.low_stock_dismissed ? "bg-secondary" : "bg-danger"
                                }`}
                              style={{ cursor: "pointer" }}
                              title={t("View Details")}
                            >
                              {item.low_stock_dismissed
                                ? t("low stock dismissed")
                                : t("low stock")}
                            </span>
                          ) : (
                            <span className="badge bg-success px-3 py-2">
                              {t("in stock")}
                            </span>
                          )}
                        </td>

                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 text-primary"
                              onClick={() => handleViewSales(item.id)}
                              title={t("View Sales History")}
                            >
                              <FiEye size={17} />
                            </Button>

                            {item.low_stock && !item.low_stock_dismissed && (
                              <Button
                                size="sm"
                                variant="secondary"
                                className="d-inline-flex align-items-center gap-1"
                                disabled={updatingId === item.id}
                                onClick={() => toggleLowStockDismissal(item, true)}
                                title={t("Dismiss from low stock list")}
                              >
                                <FiBellOff size={13} />
                                {t("Dismiss")}
                              </Button>
                            )}

                            {item.low_stock && item.low_stock_dismissed && (
                              <Button
                                size="sm"
                                variant="primary"
                                className="d-inline-flex align-items-center gap-1"
                                disabled={updatingId === item.id}
                                onClick={() => toggleLowStockDismissal(item, false)}
                                title={t("Restore to low stock list")}
                              >
                                <FiRotateCcw size={13} />
                                {t("Restore")}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={isAdmin ? "8" : "6"} className="text-center py-5">
                        <div className="empty-state">
                          <FiAlertTriangle size={48} className="text-muted mb-2" />
                          <p className="text-muted mb-0">{t("no stock data found")}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}